import React, { useState, useCallback } from 'react';
import { SafeAreaView, View, Text, StyleSheet, FlatList, TouchableOpacity, Image, ActivityIndicator, RefreshControl, StatusBar, Alert, Modal, ScrollView, TextInput } from 'react-native';
import { Ionicons, MaterialCommunityIcons } from '@expo/vector-icons';
import { useFocusEffect } from '@react-navigation/native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import axios from 'axios';

const API_BASE_URL = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';

const api = axios.create({
  baseURL: API_BASE_URL, timeout: 15000,
  headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-API-Key': API_KEY },
});
api.interceptors.request.use(async c => {
  try { const t = await AsyncStorage.getItem('auth_token')||await AsyncStorage.getItem('token'); if(t)c.headers.Authorization=`Bearer ${t}`;
    const cid = await AsyncStorage.getItem('company_id'); if(cid)c.params={...c.params,company_id:parseInt(cid)};
  }catch{} return c;
}, e=>Promise.reject(e));

const EP = { companyProfile:`${API_BASE_URL}/api/data/company_profiles`, orders:`${API_BASE_URL}/api/data/orders`, vendors:`${API_BASE_URL}/api/vendors` };

const OrdersListScreen = ({ navigation }) => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [companyLogo, setCompanyLogo] = useState(null);
  const [companyProfile, setCompanyProfile] = useState(null);
  const [vendorsMap, setVendorsMap] = useState({});
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [stats, setStats] = useState({ total:0, pending:0, confirmed:0, shipped:0, delivered:0, cancelled:0, totalValue:0 });

  useFocusEffect(useCallback(()=>{ loadCompanyProfile(); fetchOrders(); },[]));

  const loadCompanyProfile = async () => {
    try {
      const cached = await AsyncStorage.getItem('company_profile');
      if(cached){const p=JSON.parse(cached);setCompanyLogo(p.logo||null);setCompanyProfile(p);}
      const cid = await AsyncStorage.getItem('company_id');
      if(cid){
        const res = await api.get(`/api/company-profile/${cid}`);
        if(res.data?.success){
          const p=Array.isArray(res.data.data)?res.data.data[0]:res.data.data;
          if(p){setCompanyLogo(p.logo||null);setCompanyProfile(p);await AsyncStorage.setItem('company_profile',JSON.stringify(p));}
        }
      }
    }catch(e){console.warn('Logo error:',e.message);}
  };

  const fetchOrders = async () => {
    setLoading(true);
    try {
      const cid = await AsyncStorage.getItem('company_id');
      const ordersRes = await api.get(EP.orders, { params: { company_id: cid, sort_by: 'created_at', sort_order: 'DESC' } });
      let ordersData = ordersRes.data?.success && Array.isArray(ordersRes.data.data) ? ordersRes.data.data : (Array.isArray(ordersRes.data) ? ordersRes.data : []);

      const supplierIds = [...new Set(ordersData.map(o => o.supplier_id).filter(id => id != null))];
      if(supplierIds.length>0){
        try{
          const vendorsRes = await api.get(EP.vendors, { params: { company_id: cid } });
          const vendors = vendorsRes.data?.success && Array.isArray(vendorsRes.data.data) ? vendorsRes.data.data : [];
          const map = {};vendors.forEach(v=>{map[v.id]=v.company_name||v.name;});setVendorsMap(map);
        }catch{}
      }
      setOrders(ordersData);

      const st={total:ordersData.length,pending:0,confirmed:0,shipped:0,delivered:0,cancelled:0,totalValue:0};
      ordersData.forEach(o=>{
        st.totalValue+=Number(o.total_amount||0);
        const s=(o.status||'').toLowerCase();
        if(st[s]!==undefined)st[s]++;
      });
      setStats(st);
    }catch(e){console.error('Orders error:',e);}finally{setLoading(false);setRefreshing(false);}
  };

  const onRefresh=()=>{setRefreshing(true);fetchOrders();};
  const formatDate=d=>{if(!d)return '';return new Date(d).toLocaleDateString('en-GB',{day:'2-digit',month:'short',year:'numeric'});};
  const formatCurrency=v=>new Intl.NumberFormat('en-GB',{style:'currency',currency:companyProfile?.currency||'GBP',minimumFractionDigits:2,maximumFractionDigits:2}).format(v||0);
  const getStatusColor=s=>{switch(s?.toLowerCase()){case 'pending':return '#F59E0B';case 'confirmed':return '#10B981';case 'shipped':return '#0e4e96';case 'delivered':return '#8B5CF6';case 'cancelled':return '#EF4444';default:return '#64748B';}};

  const filtered=orders.filter(o=>{
    if(filterStatus!=='all'&&(o.status||'').toLowerCase()!==filterStatus)return false;
    if(searchQuery){const q=searchQuery.toLowerCase();return `${vendorsMap[o.supplier_id]||''} ${o.notes||''} ${o.id}`.toLowerCase().includes(q);}
    return true;
  });

  const renderHeader=()=>(
    <View style={s.header}>
      <TouchableOpacity style={s.backBtn} onPress={()=>navigation.canGoBack()&&navigation.goBack()}>
        <Ionicons name="chevron-back" size={24} color="#fff"/></TouchableOpacity>
      <View style={s.headerCenter}>
        {companyLogo?<Image source={{uri:companyLogo}} style={s.headerLogo}/>:<MaterialCommunityIcons name="office-building" size={30} color="#fff"/>}
        <View style={{marginLeft:10}}><Text style={s.headerTitle}>Orders</Text>
          {companyProfile?.name&&<Text style={s.headerSub}>{companyProfile.name}</Text>}</View>
      </View>
      <View style={{width:40}}/>
    </View>
  );

  const renderStats=()=>(
    <ScrollView horizontal showsHorizontalScrollIndicator={false} style={s.statsBar} contentContainerStyle={{paddingHorizontal:12}}>
      {[{l:'Total',v:stats.total,c:'#0e4e96'},{l:'Pending',v:stats.pending,c:'#F59E0B'},{l:'Confirmed',v:stats.confirmed,c:'#10B981'},
        {l:'Shipped',v:stats.shipped,c:'#0e4e96'},{l:'Delivered',v:stats.delivered,c:'#8B5CF6'},{l:'Value',v:formatCurrency(stats.totalValue),c:'#10B981'}].map((st2,i)=>(
        <View key={i} style={s.statCard}><Text style={[s.statVal,{color:st2.c}]}>{st2.v}</Text><Text style={s.statLbl}>{st2.l}</Text></View>
      ))}
    </ScrollView>
  );

  const renderFilters=()=>(
    <View style={s.filtersRow}>
      <View style={s.searchRow}><MaterialCommunityIcons name="magnify" size={18} color="#9CA3AF"/>
        <TextInput style={s.searchInput} placeholder="Search orders..." placeholderTextColor="#9CA3AF" value={searchQuery} onChangeText={setSearchQuery}/></View>
      <ScrollView horizontal showsHorizontalScrollIndicator={false} style={{marginTop:6}}>
        {['all','pending','confirmed','shipped','delivered','cancelled'].map(st2=>(
          <TouchableOpacity key={st2} style={[s.fChip,filterStatus===st2&&{backgroundColor:getStatusColor(st2)||'#0e4e96'}]} onPress={()=>setFilterStatus(st2)}>
            <Text style={[s.fChipTxt,filterStatus===st2&&{color:'#FFF'}]}>{st2==='all'?'All':st2.charAt(0).toUpperCase()+st2.slice(1)}</Text>
          </TouchableOpacity>
        ))}
      </ScrollView>
    </View>
  );

  const renderOrderCard=({item})=>(
    <TouchableOpacity style={s.orderCard} onPress={()=>{setSelectedOrder(item);setShowModal(true);}}>
      <View style={s.orderHeader}>
        <View style={s.orderInfo}><Text style={s.orderSupplier}>{vendorsMap[item.supplier_id]||`Supplier #${item.supplier_id||'N/A'}`}</Text>
          <Text style={s.orderDate}>{formatDate(item.order_date||item.created_at)}</Text></View>
        <View style={[s.statusBadge,{backgroundColor:getStatusColor(item.status)}]}><Text style={s.statusText}>{item.status||'unknown'}</Text></View>
      </View>
      <View style={s.orderDetails}><Text style={s.orderAmount}>{formatCurrency(item.total_amount)}</Text>
        {item.notes?<Text style={s.orderNotes} numberOfLines={1}>{item.notes}</Text>:null}</View>
      <View style={s.orderFooter}><Text style={s.orderId}>Order #{item.id}</Text><MaterialCommunityIcons name="chevron-right" size={20} color="#64748B"/></View>
    </TouchableOpacity>
  );

  const renderModal=()=>(
    <Modal visible={showModal} animationType="slide" presentationStyle="pageSheet" onRequestClose={()=>setShowModal(false)}>
      <View style={{flex:1,backgroundColor:'#FFF'}}>
        <View style={{flexDirection:'row',justifyContent:'space-between',alignItems:'center',padding:16,borderBottomWidth:1,borderBottomColor:'#E5E7EB'}}>
          <Text style={{fontSize:18,fontWeight:'bold',color:'#1F2937',flex:1}}>Order #{selectedOrder?.id}</Text>
          <TouchableOpacity style={{width:40,height:40,borderRadius:20,backgroundColor:'#F3F4F6',justifyContent:'center',alignItems:'center'}} onPress={()=>setShowModal(false)}>
            <MaterialCommunityIcons name="close" size={24} color="#6B7280"/></TouchableOpacity></View>
        {selectedOrder&&<ScrollView style={{flex:1,padding:16}}>
          <View style={{marginBottom:20}}><Text style={{fontSize:16,fontWeight:'600',color:'#1F2937',marginBottom:8}}>Order Details</Text>
            <Text style={{fontSize:14,color:'#6B7280',lineHeight:22}}>Supplier: {vendorsMap[selectedOrder.supplier_id]||`#${selectedOrder.supplier_id}`}</Text>
            <Text style={{fontSize:14,color:'#6B7280',lineHeight:22}}>Status: {selectedOrder.status}</Text>
            <Text style={{fontSize:14,color:'#6B7280',lineHeight:22}}>Amount: {formatCurrency(selectedOrder.total_amount)}</Text>
            <Text style={{fontSize:14,color:'#6B7280',lineHeight:22}}>Date: {formatDate(selectedOrder.order_date||selectedOrder.created_at)}</Text>
            {selectedOrder.notes&&<Text style={{fontSize:14,color:'#6B7280',lineHeight:22}}>Notes: {selectedOrder.notes}</Text>}
          </View>
          {companyProfile&&<View style={{marginBottom:20}}><Text style={{fontSize:16,fontWeight:'600',color:'#1F2937',marginBottom:8}}>Company</Text>
            <Text style={{fontSize:14,color:'#6B7280',lineHeight:22}}>{companyProfile.name}{companyProfile.industry?` · ${companyProfile.industry}`:''}</Text>
            {companyProfile.headquarters&&<Text style={{fontSize:14,color:'#6B7280',lineHeight:22}}>{companyProfile.headquarters}</Text>}
          </View>}
        </ScrollView>}
      </View>
    </Modal>
  );

  if(loading&&!refreshing)return (
    <SafeAreaView style={s.safeArea}><StatusBar barStyle="light-content" backgroundColor="#0e4e96"/>{renderHeader()}
      <View style={s.loadingContainer}><ActivityIndicator size={36} color="#0e4e96"/><Text style={s.loadingText}>Loading orders...</Text></View></SafeAreaView>
  );

  return (
    <SafeAreaView style={s.safeArea}><StatusBar barStyle="light-content" backgroundColor="#0e4e96"/>
      {renderHeader()}{renderStats()}{renderFilters()}
      <FlatList data={filtered} renderItem={renderOrderCard} keyExtractor={item=>String(item.id)}
        contentContainerStyle={s.listContainer} refreshControl={<RefreshControl refreshing={refreshing} onRefresh={onRefresh}/>}
        ListEmptyComponent={<View style={s.emptyContainer}><MaterialCommunityIcons name="package-variant" size={48} color="#CBD5E1"/>
          <Text style={s.emptyTitle}>No orders found</Text><Text style={s.emptyText}>Place an order to see it here.</Text></View>}/>
      {renderModal()}
    </SafeAreaView>
  );
};

const s = StyleSheet.create({
  safeArea:{flex:1,backgroundColor:'#0e4e96'},
  header:{backgroundColor:'#0e4e96',paddingVertical:15,paddingHorizontal:20,flexDirection:'row',alignItems:'center',justifyContent:'space-between'},
  backBtn:{padding:5},headerCenter:{flexDirection:'row',alignItems:'center'},headerLogo:{width:30,height:30,borderRadius:15,resizeMode:'cover'},headerTitle:{color:'#fff',fontSize:20,fontWeight:'bold'},headerSub:{color:'#93C5FD',fontSize:12},
  loadingContainer:{flex:1,justifyContent:'center',alignItems:'center',backgroundColor:'#f8fafc'},loadingText:{marginTop:10,fontSize:16,color:'#64748B'},
  statsBar:{backgroundColor:'#FFF',paddingVertical:10,borderBottomWidth:1,borderBottomColor:'#E5E7EB'},statCard:{alignItems:'center',marginRight:14,minWidth:60},statVal:{fontSize:15,fontWeight:'bold'},statLbl:{fontSize:10,color:'#64748B',marginTop:2},
  filtersRow:{backgroundColor:'#FFF',padding:10,borderBottomWidth:1,borderBottomColor:'#E5E7EB'},searchRow:{flexDirection:'row',alignItems:'center',backgroundColor:'#F3F4F6',borderRadius:8,paddingHorizontal:10,paddingVertical:6},searchInput:{flex:1,fontSize:13,color:'#1F2937',marginLeft:6},
  fChip:{paddingHorizontal:10,paddingVertical:5,borderRadius:14,backgroundColor:'#F3F4F6',marginRight:6},fChipTxt:{fontSize:11,color:'#6B7280',fontWeight:'500'},
  listContainer:{padding:12,backgroundColor:'#f8fafc'},
  orderCard:{backgroundColor:'#fff',borderRadius:12,padding:16,marginBottom:12,shadowColor:'#000',shadowOffset:{width:0,height:2},shadowOpacity:0.05,shadowRadius:10,elevation:2},
  orderHeader:{flexDirection:'row',justifyContent:'space-between',alignItems:'center',marginBottom:10},orderInfo:{flex:1},orderSupplier:{fontSize:16,fontWeight:'600',color:'#0f172a',marginBottom:2},orderDate:{fontSize:12,color:'#64748B'},
  statusBadge:{paddingHorizontal:10,paddingVertical:4,borderRadius:4},statusText:{color:'#fff',fontSize:12,fontWeight:'bold'},
  orderDetails:{marginBottom:10},orderAmount:{fontSize:14,fontWeight:'600',color:'#0e4e96'},orderNotes:{fontSize:12,color:'#64748B',marginTop:4},
  orderFooter:{flexDirection:'row',justifyContent:'space-between',alignItems:'center',paddingTop:8,borderTopWidth:1,borderTopColor:'#e2e8f0'},orderId:{fontSize:12,color:'#94A3B8'},
  emptyContainer:{alignItems:'center',justifyContent:'center',padding:40},emptyTitle:{fontSize:18,fontWeight:'600',color:'#0f172a',marginTop:16},emptyText:{fontSize:14,color:'#64748B',textAlign:'center',marginTop:8},
});

export default OrdersListScreen;